import { useAppForm } from "@features/form/useAppForm";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { revalidateLogic } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";
import { authClient } from "./authClient";
import { AuthRoute } from "./AuthRoute";

type ForgotPasswordPageProps = {
	email: string;
	onChangeEmail: ((email: string) => void) | undefined;
};

const ForgotPasswordSchema = z.object({
	email: z.email(),
});

export const ForgotPasswordPage = (props: ForgotPasswordPageProps) => {
	const { email, onChangeEmail } = props;
	const LL = useI18nContext().LL.auth;

	const navigate = useNavigate();
	const { show, redirectToPath } = AuthRoute.useSearch();
	const { callbackUrl } = AuthRoute.useLoaderData();

	const form = useAppForm({
		defaultValues: {
			email: email,
		},
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: ForgotPasswordSchema,
		},
		onSubmit: async ({ value }) => {
			try {
				const res = await authClient.requestPasswordReset({
					email: value.email,
					redirectTo: callbackUrl,
				});
				if (res.error) {
					throw res.error;
				}
				navigate({ to: AuthRoute.fullPath, search: { verifyEmail: value.email, show, redirectToPath } });
			} catch (error: any) {
				console.error(error);
				toast.error("message" in error ? (error.message as string) : "Something went wrong, try again later");
			}
		},
	});

	return (
		<div className="m-auto w-full max-w-sm space-y-6">
			<div className="space-y-2 text-center">
				<h1 className="text-3xl font-bold font-breit">
					<T>{LL.FORGOT_PASSWORD_TITLE()}</T>
				</h1>
				<p className="text-muted-foreground text-sm">
					<T>{LL.FORGOT_PASSWORD_DESCRIPTION()}</T>
				</p>
			</div>

			<form
				id="forgot-password"
				className="space-y-4"
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<form.AppField
					name="email"
					listeners={{
						onChange: ({ value }) => {
							onChangeEmail?.(value);
						},
					}}
				>
					{(field) => <field.Input label={LL.FORM_FIELDS_EMAIL_LABEL()} placeholder={LL.FORM_FIELDS_EMAIL_PLACEHOLDER()} />}
				</form.AppField>
			</form>

			<div className="flex flex-col space-y-4">
				<form.AppForm>
					<form.SubmitButton className="w-full" form="forgot-password">
						<T>{LL.FORGOT_PASSWORD_SUBMIT_BUTTON()}</T>
					</form.SubmitButton>
				</form.AppForm>
				<p className="text-muted-foreground text-center text-sm">
					<T>{LL.FORGOT_PASSWORD_REMEMBERED_QUESTION()}</T>{" "}
					<Link className="text-foreground underline" from="/auth" search={{ show: "sign-in" }}>
						<T>{LL.SIGN_IN()}</T>
					</Link>
				</p>
			</div>
		</div>
	);
};
